/**
 * Every sentence the agent wallet says, pinned once (00-program C6). Import these; never retype one. No em dashes,
 * never a model vendor's name: the tests in __tests__/copy.test.ts hold both rules.
 */
import type { AgentPurchaseStatus } from './types.js';

export const AGENT_MONTH_SPENT = (agent: string, month: string) =>
  `${agent}'s ${month} budget is used up. Raise it in Settings to keep going.`;
/** For a card that cannot name the agent or the month (a native turn that tripped mid-turn carries no error block). */
export const AGENT_MONTH_SPENT_GENERIC = "This agent's monthly budget is used up. Raise it in Settings to keep going.";
export const AGENT_MONTH_CARD_TITLE = (agent: string) => `${agent}'s monthly budget is used up`;
/** For a teammate's card whose agent the web's roster cannot place yet: never Bash's name on someone else's month. */
export const AGENT_MONTH_CARD_TITLE_GENERIC = "This agent's monthly budget is used up";
export const AGENT_MONTH_CARD_RAISE = 'Raise the budget';
export const AGENT_MONTH_CARD_STOP = "That's enough";

export const BUY_NO_BUDGET = 'Set a monthly budget for me in Settings and I can pay for things.';
export const BUY_OVER_BUDGET = (amount: string, left: string, month: string) =>
  `That costs ${amount} and I have ${left} left in ${month}. Raise my budget, or I can look for something cheaper.`;
export const BUY_UNSUPPORTED_CURRENCY = 'I can only pay in rupees or dollars for now.';
export const BUY_BUSY = "I'm waiting for you to finish another payment first.";

export const PURCHASE_CARD_TITLE = (amount: string, shop: string) => `Pay ${amount} to ${shop}?`;
export const PURCHASE_CARD_LEFT = (left: string, month: string) => `${left} of ${month}'s budget left after this`;
export const PURCHASE_APPROVE = 'Approve';
export const PURCHASE_DECLINE = 'Decline';
export const PURCHASE_APPROVE_IN_APP = 'Approve in Vestra';
export const PAY_BANNER = (amount: string) => `You approved ${amount}. Don't pay more than this.`;
export const PAY_DONE = "I've paid";
export const PAY_CANCEL = 'Cancel payment';
export const PAY_STILL_OPEN = 'It looks like the payment page is still open. Did you finish paying?';
export const PAY_STILL_OPEN_YES = "Yes, I'm done";
export const PAY_STILL_OPEN_NO = 'Keep paying';
export const PAY_OPEN_UPI = 'Open your UPI app';
export const PURCHASE_UNKNOWN_ASK = 'Did this payment go through?';

// Model-facing (tool results and gate refusals). Not shown to people.
export const GATE_PAY_CLICK =
  'Payments go through the buy tool. Fill in everything up to the pay button, then call buy with the shop, what you are buying and the total. Never press the pay button yourself.';
export const GATE_FROZEN_PAYING_TASK =
  'The person is paying in the browser right now. Browser and shell tools are paused. Wait for the result of buy.';
export const GATE_FROZEN_OTHER_TASK =
  'The browser is paused while the person makes a payment. Try again in a few minutes.';
/** The runner's hold on every reconnect, until the server says whether a payment is open: none is known yet. */
export const GATE_FROZEN_RECONNECTING = 'The browser is reconnecting. Try again in a few seconds.';
export const BUY_PAY_STEP_DONE =
  "The person finished the payment step. Read the shop's confirmation page now, then call buy_report with paid (the order number and the amount paid), failed, or unclear.";
export const BUY_DECLINED = 'The person declined this purchase. Nothing was paid. Ask them what they would like instead.';
export const BUY_EXPIRED = 'The person did not answer within 30 minutes, so the purchase expired. Nothing was paid.';

// ── P3: buy and pay (plan p3-buy-and-pay.md) ────────────────────────────────

export const PURCHASE_PAYING = (shop: string) => `You're paying ${shop}`;
export const PURCHASE_OPEN_BROWSER = 'Open the browser';
export const PURCHASE_APPROVE_BUSY = 'Another payment is still open. Finish it first.';
export const PURCHASE_ACTION_FAILED = "That didn't go through. Try again in a moment.";
export const PURCHASE_LOADING = 'One moment';
export const PAY_TYPE_ON_PAGE = 'Type on the page';
export const PURCHASE_CHAT_NOTE = 'Approving and paying happen in Vestra.';
/** A chat's Deny on a purchase that is past its ask (an Approve landed first): refused, never read as a decline. */
export const PURCHASE_CHAT_DENY_IN_PROGRESS = "This payment is already in progress in Vestra, so it can't be declined here. Open Vestra to see it.";
export const PURCHASE_VOICE_OPEN_APP = (amount: string, shop: string) =>
  `A payment is waiting: ${amount} to ${shop}. Open Vestra to approve and pay. I can't approve payments by voice.`;
export const PURCHASES_TITLE = 'Purchases this month';
export const PURCHASES_EMPTY = 'Nothing bought yet this month.';
/** A purchase card in Bash's "Waiting on you" (the composer's ledger): a payment, never an amount or a shop the box wrote. */
export const PURCHASE_NEED_TITLE = 'A payment is waiting for your approval';
export const PURCHASE_RESOLVE_PAID = 'It went through';
export const PURCHASE_RESOLVE_FAILED = "It didn't";
export const PURCHASE_STATUS_WORDS: Record<AgentPurchaseStatus, string> = {
  requested: 'Waiting for you',
  approved: 'Being paid',
  paying: 'Being paid',
  pay_step_done: 'Checking',
  unknown: PURCHASE_UNKNOWN_ASK,
  paid: 'Paid',
  declined: 'Declined',
  expired: 'Expired',
  failed: "Didn't go through",
  cancelled: 'Cancelled',
};

// Model-facing (tool results). Not shown to people.
export const BUY_NOT_HERE = 'buy cannot be called here. Tell the person you could not ask them to pay; nothing was paid.';
export const BUY_UNREACHABLE = "Vestra couldn't reach its payment service. Nothing was paid. Try again in a minute.";
export const BUY_NOT_AVAILABLE = "Buying isn't turned on for this account. Tell the person; nothing was paid.";
export const BUY_INVALID = 'buy needs the checkout address, the shop, what it is, the total and its currency. Nothing was paid.';
/** The server's 400 invalid_shop_url: an https address with no shop's site name left in it (its host). */
export const BUY_INVALID_SHOP_URL =
  "buy needs the full https address of the checkout page you are on, with the shop's site name in it. Nothing was paid. Call buy again with that address.";
/** The server's 404 task_not_found: the task buy was called from is not on the person's account. */
export const BUY_TASK_NOT_FOUND =
  "Vestra couldn't find this task on the person's account, so it can't ask them to pay from here. Nothing was paid. Tell the person.";
export const BUY_RAISE_FAILED = 'The purchase card could not be raised. Nothing was paid. Tell the person.';
export const BUY_ALREADY = (status: string) => `This purchase already ended (${status}). Do not call buy again for it.`;
/** buy beside another tool call in one message (the engine refuses it unrun): two cards at once can strand its answer. */
export const BUY_CALL_ALONE =
  'buy must be the only tool call in its message. Nothing was paid. Call buy again on its own, with no other tool call beside it.';
/** buy's slot when the person sends a message before answering its card: the answer still comes, as this call's result. */
export const BUY_DEFERRED =
  "The person sent a new message before answering this purchase. Their answer to it will come back here, as this call's result. Do not call buy again for it.";
export const BUY_BEING_PAID = 'The person is paying for this right now. Wait for the result of buy; do not call buy again.';
/** buy on a purchase whose pay step is done: it is owed a report, not a second card. */
export const BUY_WAITING_FOR_REPORT = (purchaseId: string) =>
  `The pay step for this purchase is done and it is waiting for buy_report. Read the shop's confirmation page, then call buy_report with purchase_id ${purchaseId}. Do not call buy again for it.`;
/** Asks the model to quote an order number only when the shop gave one; with none, there is nothing to quote. */
export const BUY_REPORTED_PAID = (amount: string, shop: string, reference: string | null) =>
  reference
    ? `Recorded: paid ${amount} to ${shop}, order ${reference}. Tell the person, with the order number.`
    : `Recorded: paid ${amount} to ${shop}. Tell the person.`;
export const BUY_REPORTED_FAILED = 'Recorded: the payment did not go through, so nothing was spent. Tell the person.';
export const BUY_REPORTED_UNCLEAR =
  "Recorded as unclear. Check the shop's orders page if you can and call buy_report again with paid or failed; otherwise ask the person whether it went through.";
// buy_report runs after the person's pay step, when money has usually moved: its words never say nothing was paid and
// never send the model back to buy (a second hold, a second card for the same order).
/** The server has no such purchase (404) or it is not waiting for a report (409). */
export const BUY_REPORT_REFUSED = "That purchase is not waiting for a report. Nothing was changed. Use the purchaseId from buy's result as purchase_id.";
/** buy_report's own arguments were wrong, on the box or at the door (400). */
export const BUY_REPORT_INVALID =
  "buy_report needs purchase_id (the purchaseId from buy's result) and outcome (paid, failed or unclear); when paid, add amount_paid and order_reference as the confirmation page shows them. Nothing was recorded yet. Call buy_report again with those.";
/** No reply, or the server could not take the report (5xx and the rest): retry the report, never buy. */
export const BUY_REPORT_UNREACHABLE =
  "Vestra couldn't record the report yet. The payment may have gone through. Call buy_report again in a minute with the same details; do not call buy again for this order.";

// Developer-facing: a Platform API caller reads it as the message of the 503 box_unavailable a browser read gets while a
// person pays (the runner's handlers/browser-cookies.ts sends it whole; the server adds nothing). A freeze can hold for
// up to PAYMENT_FREEZE_MAX_MS (35 minutes), so it never says "a few seconds".
export const BROWSER_PAUSED_FOR_PAYMENT = 'The browser is paused while a payment is open. Retry after the payment finishes.';

// Model-facing (buy's result when the server ended a pay step nobody closed). Not shown to people.
export const BUY_PAY_STEP_TIMED_OUT =
  "The payment step closed without the person confirming they paid. Check the shop's orders page if you can, then call buy_report with paid (the order number and the amount paid), failed, or unclear (unclear if you can't tell).";

// Model-facing (buy's result when the person pressed Cancel payment during the pay step). Not shown to people.
export const BUY_PAYMENT_CANCELLED =
  'The person stopped the payment step, so nothing was recorded as paid. If they say it went through, ask them about it before doing anything else.';

// Model-facing (buy's result when its details do not match the shared schema, wallet/details.ts). Not shown to people.
export const BUY_INVALID_DETAILS =
  "buy's details did not match the fields for a flight, a hotel stay or a cart. Nothing was held and nothing was paid. Call buy again with the details fixed, or without details.";
/** BUY_INVALID_DETAILS with the first few things that did not match ("legs.0.to.code: ..."), so the model can fix those fields. */
export const BUY_INVALID_DETAILS_AT = (issues: string) => `${BUY_INVALID_DETAILS} What did not match: ${issues}.`;

// ── P3 task 12: the purchase card and the Needs-you row (founder-approved v2, 2026-10-16) ───────────────────────

/** The kind word a waiting payment wears on Home's Needs-you row (mockups/agent-wallet-pay-v2b-2026-10-16.html §4). */
export const PURCHASE_NEED_KICKER = 'Payment';
/** A waiting payment's second line away from the task when its task has no title: never an amount or a shop. */
export const PURCHASE_NEED_WHY = 'The amount and the shop are in the task.';
/** Under the total when a cart's own lines do not add up to the server's amount (P3 task 17's detailsMismatch). */
export const PURCHASE_DETAILS_MISMATCH = (shop: string) =>
  `The items don't add up to the total. Check on ${shop}'s page.`;
/**
 * The still-open question as the approved pay screens word it (mockups/agent-wallet-pay-v2b-2026-10-16.html §3): the
 * lead, then the ask. Its buttons are PAY_STILL_OPEN_NO and PAY_STILL_OPEN_PAID.
 */
export const PAY_STILL_OPEN_LEAD = 'The payment page still looks open.';
export const PAY_STILL_OPEN_ASK = (agent: string) =>
  `Did you finish paying? If you're still on it, keep going; ${agent} stays paused.`;
/** For a surface that cannot name the agent. */
export const PAY_STILL_OPEN_ASK_GENERIC =
  "Did you finish paying? If you're still on it, keep going; your agents stay paused.";
export const PAY_STILL_OPEN_PAID = "Yes, I've paid";

// ── P3 task 13: the pay bar (fix round 1) ───────────────────────────────────────────────────────────────────────────

/**
 * Cancel during a pay step asks once, in place, before the purchase is cancelled: a cancel after the person has paid
 * on the shop's page would give the hold back and count less spent this month than went out. Its buttons are
 * PAY_STILL_OPEN_NO (Keep paying) and PAY_CANCEL (Cancel payment).
 */
export const PAY_CANCEL_CONFIRM = (shop: string) =>
  `Cancel this payment? If you've already paid on ${shop}'s page, tap I've paid instead.`;
